import { useState, useEffect, ChangeEvent, FormEvent } from "react";
import ParcelService from "../../services/parcelService";
import { ParcelData } from "../../interfaces/ParcelData";
import { FieldType } from "../../interfaces/FieldType";
import { useSharedState } from "../../config/FarmContext";
import { Snackbar } from "../common/Snackbar";
import { SnackbarProps } from "../common/SnackbarProps";
import "./BasicForm.css"

interface FormProps {
    onSave: (response: SnackbarProps) => void;
    onEdit?: () => void;
    parcel: ParcelData;
}


const ParcelForm = (props: FormProps) => {
    const { farm } = useSharedState();
    const [formData, setFormData] = useState<ParcelData>({
        name: props.parcel?.name || "Neue Parzelle",
        farmId: farm?.id,
        type: props.parcel?.type || FieldType.Ackerbau,
        subtype: props.parcel?.subtype || "",
        area: 0,
        geometry: props.parcel?.geometry || ""
    });

    const [color, setColor] = useState<string>();
    const [isSubmitting, setIsSubmitting] = useState(false); 
    const [snackbar, setSnackbar] = useState<SnackbarProps>({ active: false })

    const parcelService = new ParcelService();

    useEffect(() => {
        if (!props.parcel || !props.parcel.geometry || !props.parcel.id) {
            setFormData({
                name: props.parcel?.name || "Neue Parzelle",
                farmId: farm?.id,
                type: props.parcel?.type || FieldType.Ackerbau,
                subtype: props.parcel?.type || "",
                area: 0,
                geometry: props.parcel?.geometry || "{}"
            })
            setColor("#1b4a07")
            return;
        }

        const layer = JSON.parse(props.parcel.geometry)
        const id = Number(props.parcel.id)
        setColor(getColorFormProps())
        if (!id) {
            setFormData((prev) => ({
                ...prev,
                geometry: props.parcel.geometry,
            }));
            return;
        }

        const fetchParcelData = async () => {
            try {
                const data = await parcelService.getParcelById(id);
                setFormData({ ...data, geometry: layer.layerId })
            } catch (error) {
                console.error("Error fetching parcel data:", error);
            }
        };

        fetchParcelData();
    }, [props.parcel]);

    const triggerSnackbar = (message: string, type: "success" | "error" | "info" | "warning") => {
        setSnackbar({ message: message, type: type, active: true });
        setTimeout(() => {
            setSnackbar({ active: false });
        }, 3000);
    };

    const getColorFormProps = () => {
        if (!props.parcel?.geometry) return;
        return JSON.parse(props.parcel.geometry).properties?.color;
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
    
        if (isSubmitting) return;
        setIsSubmitting(true);
    
        try {
            if (formData.id && props.onEdit) {
                await parcelService.editById(formData.id.toString(), formData);
                triggerSnackbar("Parzelle erfolgreich bearbeitet ✅", "success");
                props.onEdit();
            } else {
                await parcelService.createParcel({ ...formData, farmId: farm?.id });
                props.onSave({ message: "Parzelle erfolgreich erstellt ✅", type: "success" });
            }
        } catch (error: any) {

            var errorMessage = "";
            switch(error.response?.data.code){
                case 1:
                    errorMessage = "Limit erreicht. Ändere deine Aboeinstellungen um weitere Parzellen zu erstellen ❌"
                    break;
                case 2: 
                    errorMessage = "Keine Berechtigung. Ändere deine Aboeinstellungen um weitere Parzellen zu erstellen ❌"
                    break;
                default:
                    errorMessage = formData.id
                ? "Änderungen konnten nicht gespeichert werden ❌"
                : "Parzelle konnte nicht erstellt werden ❌";

            }

            triggerSnackbar(errorMessage, "error");
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleColorChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        let layer = props.parcel?.geometry && JSON.parse(props.parcel?.geometry) || {};
        if(Object.keys(layer).length)
            layer.properties.color = value;
        setFormData((prevData) => ({
            ...prevData,
             geometry: JSON.stringify(layer),
        }));
        setColor(value)
    };

    return (
        <div className="bg-white p-5 rounded-lg shadow-md min-w-[400px] min-h-[500px]">
            <h1 className="text-xl font-bold pb-3 text-gray-800">Parzelle</h1>
            <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                    <label htmlFor="name" className="text-sm font-semibold text-gray-700 mb-1">Name</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="py-1.5 px-3 w-full rounded-lg bg-gray-50 border border-gray-200 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#37813b]"
                    />
                </div>
                <div>
                    <label htmlFor="type" className="text-sm font-semibold text-gray-700 mb-1">Feldtyp</label>
                    <select
                        id="type"
                        name="type"
                        value={formData.type}
                        onChange={handleChange}
                        className="py-1.5 px-3 w-full rounded-lg bg-gray-50 border border-gray-200 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#37813b]"
                    >
                        <option value={FieldType.Ackerbau}>Ackerbau</option>
                        <option value={FieldType.Futterbau}>Futterbau</option>
                    </select>

                </div>
                <div>
                    <label htmlFor="area" className="text-sm font-semibold text-gray-700 mb-1">{formData.type === FieldType.Ackerbau ? "Subtyp" : "Kultur"}</label>
                    <input
                        type="text"
                        id="subtype"
                        name="subtype"
                        value={formData.subtype ?? ""}
                        onChange={handleChange}
                        className="py-1.5 px-3 w-full rounded-lg bg-gray-50 border border-gray-200 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#37813b]"
                    />
                </div>

                <div>
                    <label htmlFor="area" className="text-sm font-semibold text-gray-700 mb-1">Fläche (a)</label>
                    <input
                        type="number"
                        id="area"
                        name="area"
                        value={formData.area}
                        onChange={handleChange}
                        required
                        className="py-1.5 px-3 w-full rounded-lg bg-gray-50 border border-gray-200 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#37813b]"
                    />
                </div>
                <div>
                    <label htmlFor="color" className="text-sm font-semibold text-gray-700 mb-1 block">Farbe</label>
                    <input
                        type="color"
                        id="color"
                        name="color"
                        className="w-[100px] h-[40px] cursor-pointer p-[3px] rounded-[10px] border border-gray-300 bg-transparent"
                        value={color || '#1b4a07'}
                        onChange={handleColorChange}
                    />
                </div>

                <button className="btn rounded-lg" type="submit" disabled={isSubmitting}>Speichern</button>
            </form>
            {snackbar.active && <Snackbar message={snackbar.message!} type={snackbar.type} />}
        </div>
    );
}

export default ParcelForm;
